// =============================================================================
// explain.ts — le panneau « IA du projet » : explications tirées de l'état MESURÉ
// du projet (CRM-PROJECT-ERP-MODEL.md §20.23 / §20.25). Fonctions pures, sans I/O.
//
// Ce n'est PAS un modèle de langage : chaque phrase est construite à partir de faits
// que le serveur ou le poste ont réellement observés, et le panneau le dit. Donc :
//   - aucune invention : un fait absent s'énonce « non mesuré », jamais deviné ;
//   - aucun « conforme » : 3-2-1 reste « incomplet » tant que copies, supports et
//     hors-site ne sont pas constatés ; RTO et RPO sont des mesures d'UN test ;
//   - la phrase de récupération n'entre jamais ici : seul son état (confirmée ou non) ;
//   - une question ne modifie RIEN. Une action est PROPOSÉE (proposals) ; l'exécuter
//     exige la validation de l'usager, puis une vérification (voir le panneau).
// =============================================================================

export interface TestFacts {
  status: 'RUNNING' | 'PASSED' | 'FAILED'
  startedAt: string
  finishedAt: string | null
  snapshotAt: string | null
  filesExpected: number | null; filesRestored: number | null; filesVerified: number | null
  blocksTotal: number | null; blocksVerified: number | null
  durationMs: number | null; rpoSeconds: number | null
  originalsModified: boolean
  error: string | null
  /** MANUAL (bouton) ou SCHEDULED (test automatique) ; absent pour les tests d'avant la périodicité. */
  trigger?: 'MANUAL' | 'SCHEDULED' | null
  cleanedUp?: boolean
}

export interface BackupFacts {
  hasSet: boolean
  hasFolders: boolean
  copiesTarget: number | null
  retention: { recentDays: number; historyMonths: number } | null
  escrow: boolean
  snapshots: { count: number; latestAt: string | null; latestBlocks: number | null; latestBytes: string | null }
  protection: { actual: number; target: number; complete: boolean } | null
  /** État 3-2-1 mesuré (§20.27) : une ligne factuelle du serveur ; absent = pas d'instantané complet. */
  threeTwoOne?: { summary: string } | null
  lastPass: { at: string; status: string | null; healthy: boolean; stale: boolean } | null
  recovery: { configured: boolean; confirmedAt: string | null }
  lastTest: TestFacts | null
  /** Test de restauration automatique : tous les N jours, ou null = manuel seulement. */
  schedule: { everyDays: number | null; state: string; nextDueAt: string | null }
  fullyProtected: boolean
}

export interface ProjectFacts {
  projectId: string
  name: string
  typeKey: string
  typeLabel: string
  status: string
  purpose: string | null
  capabilities: string[]
  agents: string[]
  folders: string[]
  canManage: boolean
  backup: BackupFacts | null      // null = ce type n'a pas la sauvegarde de données
}

export type Intent = 'status' | 'why_alert' | 'todo' | 'rto_rpo' | 'three_two_one' | 'last_test' | 'schedule' | 'recovery' | 'capabilities' | 'unknown'

export interface Answer { paragraphs: string[]; basis: string[]; intent: Intent }

export interface Proposal {
  id: 'restore_test' | 'confirm_recovery' | 'create_set' | 'schedule_tests'
  title: string
  why: string
  /** Texte de la boîte de confirmation : ce que l'action fait, et ce qu'elle ne fait pas. */
  confirmText: string
}

export const TEST_STALE_DAYS = 30

export const SUGGESTED: { intent: Intent; label: string }[] = [
  { intent: 'status', label: 'Où en est ce projet ?' },
  { intent: 'why_alert', label: 'Pourquoi est-il en alerte ?' },
  { intent: 'todo', label: 'Qu’est-ce qui reste à faire ?' },
  { intent: 'last_test', label: 'Qu’a donné le dernier test de restauration ?' },
  { intent: 'schedule', label: 'Mes tests de restauration sont-ils automatiques ?' },
  { intent: 'rto_rpo', label: 'Que veulent dire RTO et RPO ?' },
  { intent: 'three_two_one', label: 'Où en est le 3-2-1 ?' },
  { intent: 'recovery', label: 'Et ma phrase de récupération ?' },
]

// ─── Reconnaître la question (mots-clés ; sinon « unknown », jamais une réponse inventée) ───

const NORMALIZE = (s: string) => s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')

export function intentOf(question: string): Intent {
  const q = NORMALIZE(question)
  if (/\b(rto|rpo)\b/.test(q)) return 'rto_rpo'
  if (/3\s?-\s?2\s?-\s?1|trois deux un|regle 3/.test(q)) return 'three_two_one'
  if (/phrase|recuperation|cle\b|secours/.test(q)) return 'recovery'
  if (/periodi|automatique|programm|planif|chaque (semaine|mois|trimestre)|echeance/.test(q)) return 'schedule'
  if (/dernier test|test de restauration|restauration test|resultat du test|teste/.test(q)) return 'last_test'
  if (/pourquoi|alerte|probleme|manque|incomplet|inquiet|danger|risque/.test(q)) return 'why_alert'
  if (/reste|faire|faut|prochaine|etape|devrais|conseil/.test(q)) return 'todo'
  if (/capacite|peut|sait faire|fonction/.test(q)) return 'capabilities'
  if (/ou en|etat|statut|resume|explique|situation|va.t.il|marche|fonctionne|bien/.test(q)) return 'status'
  return 'unknown'
}

// ─── Aides de formulation ────────────────────────────────────────────────────

type Fmt = (iso: string | null | undefined) => string
const defaultFmt: Fmt = iso => (iso ? new Date(iso).toLocaleString('fr-CA', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'date inconnue')

export const duration = (ms: number) =>
  ms < 1000 ? `${ms} ms` : ms < 60_000 ? `${(ms / 1000).toFixed(1).replace('.', ',')} s` : `${Math.floor(ms / 60_000)} min ${Math.round((ms % 60_000) / 1000)} s`

export const rpoSentence = (s: number | null) =>
  s === null ? 'non mesuré'
    : s === 0 ? '0 seconde : aucune modification postérieure à l’instantané testé'
    : `${s < 120 ? `${s} secondes` : s < 7200 ? `${Math.round(s / 60)} minutes` : `${(s / 3600).toFixed(1).replace('.', ',')} heures`} de modifications postérieures à l’instantané, pas encore sauvegardées`

const bytesText = (n: string | null) => {
  if (n === null) return null
  const v = Number(n)
  return v >= 1048576 ? `${(v / 1048576).toFixed(1).replace('.', ',')} Mo` : v >= 1024 ? `${(v / 1024).toFixed(1).replace('.', ',')} Ko` : `${v} octets`
}

const PASS_TEXT: Record<string, string> = {
  COMMITTED: 'un nouvel instantané a été enregistré', UNCHANGED: 'aucun changement n’a été détecté depuis le dernier instantané',
  PARTIAL: 'l’envoi a été interrompu et sera repris à la passe suivante', REBUILD: 'un instantané a été abandonné et sera reconstruit',
  ERROR: 'la passe s’est terminée en erreur', KEY_MISSING: 'la clé de chiffrement est absente du poste',
  KEY_MISMATCH: 'la clé du poste n’est pas celle de ce jeu de sauvegarde',
}

function testAgeDays(t: TestFacts, now: number): number | null {
  return t.finishedAt ? (now - new Date(t.finishedAt).getTime()) / 86_400_000 : null
}

/**
 * La dernière restauration testée est-elle réussie ET assez récente ? Seuil : la périodicité programmée + 2 jours de
 * grâce, ou 30 jours sans programmation (même règle que le serveur : restoreTestStanding).
 */
export function testIsFresh(t: TestFacts | null, now = Date.now(), everyDays: number | null = null): boolean {
  if (!t || t.status !== 'PASSED') return false
  const age = testAgeDays(t, now)
  return age !== null && age <= (everyDays ? everyDays + 2 : TEST_STALE_DAYS)
}

const scheduleText = (s: BackupFacts['schedule'], fmt: Fmt) =>
  s.everyDays
    ? `Un test de restauration automatique est programmé tous les ${s.everyDays} jours${s.nextDueAt ? ` (prochain : ${fmt(s.nextDueAt)})` : ''}.`
    : 'Les tests de restauration sont manuels : aucun test automatique n’est programmé.'

// ─── Les alertes, une par une, avec leur raison ───────────────────────────────

export function alerts(f: ProjectFacts, now = Date.now(), fmt: Fmt = defaultFmt): string[] {
  const b = f.backup
  if (!b) return []
  const out: string[] = []
  if (!b.hasSet) { out.push('Aucun jeu de sauvegarde n’existe : rien n’est sauvegardé pour l’instant.'); return out }
  if (!b.lastPass) out.push('La dernière vérification par le poste n’est pas encore remontée : « sauvegarde récente » ne peut pas être jugée.')
  else if (!b.lastPass.healthy) out.push(`La dernière passe du poste (${fmt(b.lastPass.at)}) n’a pas abouti : ${PASS_TEXT[b.lastPass.status ?? ''] ?? b.lastPass.status ?? 'issue inconnue'}.`)
  else if (b.lastPass.stale) out.push(`Le poste n’a pas fait de passe depuis plus de 6 heures (dernière : ${fmt(b.lastPass.at)}) : est-il éteint ?`)
  if (b.snapshots.count === 0) out.push('Aucun instantané complet n’existe encore.')
  if (b.protection && !b.protection.complete) out.push(`Seulement ${b.protection.actual} copie${b.protection.actual > 1 ? 's' : ''} vérifiée${b.protection.actual > 1 ? 's' : ''} sur ${b.protection.target} visées : la troisième copie n’existe pas encore (un autre poste ou un pair doit la recevoir ; cette fonction n’est pas encore disponible).`)
  if (!b.recovery.configured) out.push('La phrase de récupération n’est pas confirmée : sans elle, la sauvegarde ne se récupère pas si le poste est perdu.')
  const t = b.lastTest
  if (!t) out.push('Aucune restauration n’a été testée : une sauvegarde jamais restaurée n’est pas une preuve.')
  else if (t.status === 'FAILED') out.push(`Le dernier test de restauration a échoué${t.error ? ` (${t.error})` : ''}.`)
  else if (t.status === 'RUNNING') out.push('Un test de restauration est en cours : son résultat n’est pas encore connu.')
  else if (!testIsFresh(t, now, b.schedule.everyDays)) {
    out.push(b.schedule.everyDays
      ? `Le test de restauration automatique est en retard (programmé tous les ${b.schedule.everyDays} jours, dernier réussi le ${fmt(t.finishedAt)}) : le poste est-il éteint ?`
      : `Le dernier test réussi date de plus de ${TEST_STALE_DAYS} jours : refaites-en un.`)
  }
  out.push('Le 3-2-1 est incomplet : les supports différents et la copie hors site ne sont pas constatés (« à vérifier »).')
  return out
}

// ─── Réponses ────────────────────────────────────────────────────────────────

export function answer(intent: Intent, f: ProjectFacts, now = Date.now(), fmt: Fmt = defaultFmt): Answer {
  const b = f.backup
  const basis: string[] = []
  const P: string[] = []

  const header = `Projet « ${f.name} » — type ${f.typeLabel}.`
  const noBackup = !b

  switch (intent) {
    case 'capabilities':
      basis.push('type du projet', 'capacités du type')
      P.push(header)
      P.push(f.capabilities.length ? `Capacités de ce type : ${f.capabilities.join(', ')}.` : 'Ce type n’a aucune capacité particulière.')
      P.push('Une capacité dit ce que le projet PEUT faire ; elle ne déclenche rien toute seule.')
      break

    case 'recovery':
      if (noBackup) { P.push('Ce projet n’a pas de sauvegarde de données : il n’y a pas de phrase de récupération.'); break }
      basis.push('état de la phrase de récupération')
      if (b.recovery.configured) {
        P.push(`Votre phrase de récupération est confirmée (${fmt(b.recovery.confirmedAt)}).`)
        P.push('Je ne la connais pas et je ne peux pas la montrer : elle n’a jamais quitté votre poste, ni vers Amiral Blanc ni vers Tower. Seule la confirmation que vous l’avez notée a été enregistrée.')
      } else {
        P.push('Votre phrase de récupération n’est pas confirmée.')
        P.push('Elle a été déposée dans un fichier privé de votre profil sur le poste (dossier Recovery de .amiralblanc). Notez-la dans un endroit sûr, hors de l’ordinateur, puis confirmez. Je ne peux pas la lire ni vous la montrer.')
      }
      break

    case 'rto_rpo': {
      basis.push('dernier test de restauration')
      P.push('Le RTO est le temps qu’il faut pour récupérer vos données ; le RPO est la quantité de travail que vous pourriez perdre (l’écart entre la dernière modification et le dernier instantané récupérable).')
      const t = b?.lastTest
      if (t && t.status === 'PASSED') {
        P.push(`Mesures du test du ${fmt(t.finishedAt)} : RTO observé ${t.durationMs !== null ? duration(t.durationMs) : 'non mesuré'} ; RPO observé ${rpoSentence(t.rpoSeconds)}.`)
      } else {
        P.push('Aucune valeur n’est disponible : elles se mesurent seulement lors d’un test de restauration réussi, et je n’en invente pas.')
      }
      P.push('Ce sont des mesures observées lors d’UN test, sur votre réseau, avec vos données actuelles. Ce ne sont pas des garanties : une restauration beaucoup plus grosse, ou à distance, prendrait plus de temps.')
      break
    }

    case 'three_two_one': {
      basis.push('copies vérifiées', 'supports', 'copie hors site')
      P.push('La règle 3-2-1 demande 3 copies de vos données, sur 2 types de supports différents, dont 1 copie hors site ou hors ligne.')
      if (noBackup) { P.push('Ce projet n’a pas de sauvegarde de données.'); break }
      const p = b.protection
      if (b.threeTwoOne) P.push(`Mesuré aujourd’hui — ${b.threeTwoOne.summary}.`)
      else P.push(`Aujourd’hui : ${p ?`${p.actual} copie${p.actual > 1 ? 's' : ''} vérifiée${p.actual > 1 ? 's' : ''} sur ${p.target} visées` : 'copies non mesurées'} (le poste et Tower).`)
      P.push('Les deux autres critères — des supports différents et une copie hors site — ne peuvent pas être constatés par Amiral Blanc pour l’instant : ils restent « à vérifier ».')
      P.push('Le 3-2-1 est donc incomplet. Je ne le déclarerai pas atteint tant qu’il n’est pas constaté.')
      break
    }

    case 'schedule': {
      if (noBackup) { P.push('Ce projet n’a pas de sauvegarde de données, donc pas de test de restauration.'); break }
      basis.push('périodicité des tests', 'dernier test de restauration')
      P.push(scheduleText(b.schedule, fmt))
      if (b.schedule.everyDays) {
        P.push('Chaque test est décidé par Amiral Blanc (échéance) et exécuté par le poste : il restaure dans un dossier isolé, vérifie tout et remonte son rapport. Réussi, il retire lui-même son dossier de test ; échoué, il le garde pour que vous l’examiniez, le signale, et se retente le lendemain.')
        if (b.schedule.state === 'OVERDUE') P.push('Le test est actuellement EN RETARD : le poste est peut-être éteint ou déconnecté.')
        if (b.schedule.state === 'FAILED') P.push('Le dernier test a échoué : il sera retenté dans les 24 heures.')
      } else {
        P.push('Sans test automatique, la restauration n’est prouvée qu’au moment de votre dernier test manuel, et cette preuve vieillit. Je peux vous proposer d’en programmer un (rien ne part sans votre validation).')
      }
      const t = b.lastTest
      P.push(t ? `Dernier test : ${t.status === 'PASSED' ? 'réussi' : t.status === 'FAILED' ? 'ÉCHOUÉ' : 'en cours'} (${t.trigger === 'SCHEDULED' ? 'automatique' : 'manuel'}${t.finishedAt ? `, ${fmt(t.finishedAt)}` : ''}).` : 'Aucun test n’a encore été fait.')
      break
    }

    case 'last_test': {
      if (noBackup) { P.push('Ce projet n’a pas de sauvegarde de données, donc pas de test de restauration.'); break }
      basis.push('dernier test de restauration')
      const t = b.lastTest
      if (!t) { P.push('Aucun test de restauration n’a été fait. Je peux vous en proposer un (isolé : vos données d’origine ne sont pas touchées).'); break }
      if (t.status === 'RUNNING') { P.push(`Un test est en cours depuis ${fmt(t.startedAt)} : son résultat n’est pas encore remonté.`); break }
      if (t.status === 'FAILED') {
        P.push(`Le dernier test (${fmt(t.finishedAt)}) a ÉCHOUÉ${t.error ? ` : ${t.error}` : ''}.`)
        P.push('Un test échoué n’est jamais présenté comme réussi, même en partie : tant qu’un test n’a pas réussi, la restauration n’est pas prouvée.')
        break
      }
      P.push(`Une restauration complète a été testée le ${fmt(t.finishedAt)} avec succès : ${t.filesRestored} fichier${(t.filesRestored ?? 0) > 1 ? 's' : ''} sur ${t.filesExpected} reconstruit${(t.filesExpected ?? 0) > 1 ? 's' : ''}, ${t.blocksVerified} bloc${(t.blocksVerified ?? 0) > 1 ? 's' : ''} sur ${t.blocksTotal} vérifié${(t.blocksVerified ?? 0) > 1 ? 's' : ''}.`)
      P.push(`Contenu confirmé par empreinte : ${t.filesVerified} / ${t.filesExpected}.${t.filesVerified !== null && t.filesExpected !== null && t.filesVerified < t.filesExpected ? ' Les autres fichiers ont leurs blocs vérifiés, mais aucune empreinte d’origine à comparer (instantané plus ancien ou original modifié depuis).' : ''}`)
      P.push(`RTO observé : ${t.durationMs !== null ? duration(t.durationMs) : 'non mesuré'} ; RPO observé : ${rpoSentence(t.rpoSeconds)}.`)
      P.push(t.originalsModified ? 'Un fichier source a changé pendant le test (pas forcément à cause de lui).' : 'Vos données d’origine n’ont pas été modifiées pendant le test.')
      if (t.trigger === 'SCHEDULED') P.push(`C’était un test automatique.${t.cleanedUp ? ' Réussi, il a retiré lui-même son dossier de test.' : ''}`)
      P.push('Ces chiffres sont des mesures de ce test, pas des garanties.')
      break
    }

    case 'why_alert': {
      basis.push('toutes les vérifications de la page')
      const list = alerts(f, now, fmt)
      if (noBackup) { P.push('Ce projet n’a pas de sauvegarde de données : aucune alerte de protection ne s’applique.'); break }
      P.push(list.length === 1 && list[0].startsWith('Le 3-2-1') ? 'Il ne reste qu’un point ouvert :' : `${list.length} point${list.length > 1 ? 's' : ''} ouvert${list.length > 1 ? 's' : ''} :`)
      for (const a of list) P.push(`• ${a}`)
      P.push('Poser cette question ne modifie rien.')
      break
    }

    case 'todo': {
      basis.push('alertes', 'actions possibles')
      if (noBackup) { P.push('Ce projet n’a pas de sauvegarde de données. Ses suggestions de missions et de tâches se trouvent plus bas sur la page ; rien n’est créé sans votre validation.'); break }
      const ps = proposals(f, now)
      const manual: string[] = []
      if (b.protection && !b.protection.complete) manual.push('Ajouter une troisième copie vérifiée : pas encore possible dans Amiral Blanc (étape à venir), il faudra un autre poste autorisé ou un pair.')
      manual.push('Vérifier quels supports et quelle copie hors site vous avez réellement (3-2-1) : à constater, Amiral Blanc ne peut pas le faire seul.')
      if (ps.length === 0 && manual.length === 0) { P.push('Rien d’urgent.'); break }
      if (ps.length) { P.push('Ce que je peux vous proposer (rien ne part sans votre clic) :'); for (const p of ps) P.push(`• ${p.title} — ${p.why}`) }
      P.push('Ce qui dépend de vous ou d’une étape à venir :')
      for (const m of manual) P.push(`• ${m}`)
      break
    }

    case 'status': {
      basis.push('type du projet', 'dernière passe', 'instantanés', 'copies vérifiées', 'phrase de récupération', 'dernier test')
      P.push(header)
      if (noBackup) {
        P.push(f.purpose ? `But : ${f.purpose}` : 'Le but n’est pas encore défini.')
        P.push(`Capacités : ${f.capabilities.length ? f.capabilities.join(', ') : 'aucune'}.`)
        break
      }
      if (!b.hasSet) { P.push('Aucun jeu de sauvegarde n’est créé : rien n’est sauvegardé.'); break }
      const lp = b.lastPass
      P.push(lp ? `Dernière passe du poste (${fmt(lp.at)}) : ${PASS_TEXT[lp.status ?? ''] ?? lp.status ?? 'issue inconnue'}.` : 'La dernière passe du poste n’est pas encore remontée.')
      if (b.snapshots.latestAt) P.push(`Dernier instantané complet : ${fmt(b.snapshots.latestAt)}${b.snapshots.latestBlocks !== null ? ` — ${b.snapshots.latestBlocks} bloc${b.snapshots.latestBlocks > 1 ? 's' : ''}` : ''}${bytesText(b.snapshots.latestBytes) ? `, ${bytesText(b.snapshots.latestBytes)}` : ''}, tous vérifiés par Tower.`)
      if (b.threeTwoOne) P.push(`État 3-2-1 mesuré — ${b.threeTwoOne.summary}. Ce qui est « non mesuré » ne l’est pas parce que c’est bon ou mauvais : aucun moyen de le constater n’existe encore.`)
      else if (b.protection) P.push(`Copies vérifiées : ${b.protection.actual} sur ${b.protection.target}.${b.protection.complete ? '' : ' Il manque la troisième : le 3-2-1 reste incomplet.'}`)
      const t = b.lastTest
      if (t && t.status === 'PASSED') {
        P.push(`Une restauration complète a été testée le ${fmt(t.finishedAt)} avec succès : ${t.filesRestored}/${t.filesExpected} fichiers, ${t.blocksVerified}/${t.blocksTotal} blocs vérifiés, RTO observé ${t.durationMs !== null ? duration(t.durationMs) : 'non mesuré'}, RPO observé ${t.rpoSeconds === 0 ? '0 seconde' : rpoSentence(t.rpoSeconds)} pour ce test.`)
      } else if (t && t.status === 'FAILED') P.push(`Le dernier test de restauration a échoué${t.error ? ` : ${t.error}` : ''}.`)
      else if (t && t.status === 'RUNNING') P.push('Un test de restauration est en cours.')
      else P.push('Aucune restauration n’a encore été testée.')
      P.push(scheduleText(b.schedule, fmt))
      P.push(b.recovery.configured ? 'La phrase de récupération est confirmée.' : 'La phrase de récupération n’est pas encore confirmée.')
      P.push(b.fullyProtected ? 'Toutes les vérifications mesurées sont passées ; le 3-2-1 reste cependant à constater (supports, hors site).' : 'La protection est incomplète : demandez-moi pourquoi pour voir le détail.')
      break
    }

    default:
      P.push('Je ne sais répondre qu’à des questions sur l’état de ce projet (où il en est, pourquoi une alerte, ce qui reste à faire, le dernier test, RTO/RPO, 3-2-1, la phrase de récupération).')
      P.push('Je ne suis pas un modèle de langage : je n’invente pas de réponse. Choisissez une question ci-dessous.')
  }
  return { paragraphs: P, basis, intent }
}

// ─── Actions PROPOSÉES — jamais exécutées par une question ────────────────────

export function proposals(f: ProjectFacts, now = Date.now()): Proposal[] {
  const b = f.backup
  if (!b || !f.canManage) return []
  const out: Proposal[] = []

  if (!b.hasSet) {
    if (b.hasFolders) out.push({
      id: 'create_set', title: 'Créer le jeu de sauvegarde',
      why: 'sans lui, rien n’est sauvegardé ; les dossiers rattachés au projet en sont la source.',
      confirmText: 'Créer le jeu de sauvegarde de ce projet ?\n\n3 copies visées, rétention 30 jours puis 12 mois, sans séquestre. Rien n’est envoyé tant que le poste n’a pas pris le jeu.',
    })
    return out
  }

  if (!b.recovery.configured) out.push({
    id: 'confirm_recovery', title: 'Confirmer que vous avez noté votre phrase de récupération',
    why: 'sans elle, la sauvegarde ne se récupère pas si le poste est perdu.',
    confirmText: 'Avez-vous noté votre phrase de récupération dans un endroit sûr, hors de cet ordinateur ?\n\nLe poste supprimera alors le fichier qui la contient. S’il ne vous l’a pas encore présentée, il refusera.',
  })

  const t = b.lastTest
  const canTest = b.snapshots.count > 0 && t?.status !== 'RUNNING'
  if (canTest && !testIsFresh(t, now, b.schedule.everyDays)) out.push({
    id: 'restore_test', title: 'Lancer un test de restauration isolé',
    why: !t ? 'aucune restauration n’a été testée.' : t.status === 'FAILED' ? 'le dernier test a échoué.' : `le dernier test réussi date de plus de ${b.schedule.everyDays ? b.schedule.everyDays + 2 : TEST_STALE_DAYS} jours.`,
    confirmText: 'Tester la restauration ?\n\nLe poste restaure le dernier instantané dans un dossier NEUF (Restaurations/Test/…), vérifie chaque bloc et chaque fichier, puis vous donne un rapport.\n\nVos données d’origine ne sont ni modifiées ni remplacées. Le dossier de test est conservé : vous pourrez l’ouvrir, puis le supprimer vous-même.',
  })

  // Sans test automatique, la restauration n'est prouvée qu'au moment du dernier test manuel — et vieillit.
  if (!b.schedule.everyDays && b.snapshots.count > 0) out.push({
    id: 'schedule_tests', title: 'Programmer un test de restauration automatique, chaque mois',
    why: 'un test jamais refait vieillit : la restauration n’est prouvée qu’au moment du test.',
    confirmText: 'Programmer un test de restauration automatique chaque mois ?\n\nLe poste restaure le dernier instantané dans un dossier isolé (Restaurations/Test/auto-…), vérifie tout et remonte son rapport. Réussi, il retire lui-même ce dossier de test ; échoué, il le garde pour que vous l’examiniez, et il le signale.\n\nVos données d’origine ne sont jamais modifiées. Vous pouvez changer la périodicité ou revenir au manuel à tout moment.',
  })
  return out
}

/** Ce que le panneau vérifie après une action validée (« exécution → vérification »). */
export function verification(id: Proposal['id'], f: ProjectFacts, since: number, fmt: Fmt = defaultFmt): { done: boolean; ok: boolean; text: string } {
  const b = f.backup
  if (!b) return { done: false, ok: false, text: '' }
  if (id === 'restore_test') {
    const t = b.lastTest
    if (!t || new Date(t.startedAt).getTime() < since - 5000) return { done: false, ok: false, text: 'Test demandé au poste ; j’attends son rapport.' }
    if (t.status === 'RUNNING') return { done: false, ok: false, text: 'Le poste restaure et vérifie…' }
    return t.status === 'PASSED'
      ? { done: true, ok: true, text: `Vérifié : test réussi (${t.filesRestored}/${t.filesExpected} fichiers, ${t.blocksVerified}/${t.blocksTotal} blocs, RTO observé ${t.durationMs !== null ? duration(t.durationMs) : 'non mesuré'}).` }
      : { done: true, ok: false, text: `Le test a échoué${t.error ? ` : ${t.error}` : ''}.` }
  }
  if (id === 'confirm_recovery') {
    return b.recovery.configured
      ? { done: true, ok: true, text: `Vérifié : la phrase est confirmée (${fmt(b.recovery.confirmedAt)}).` }
      : { done: false, ok: false, text: 'Demande envoyée au poste ; j’attends sa confirmation (il refuse si la phrase ne vous a pas encore été présentée).' }
  }
  if (id === 'schedule_tests') {
    return b.schedule.everyDays
      ? { done: true, ok: true, text: `Vérifié : ${scheduleText(b.schedule, fmt)}` }
      : { done: false, ok: false, text: 'Programmation demandée…' }
  }
  return b.hasSet
    ? { done: true, ok: true, text: 'Vérifié : le jeu de sauvegarde existe ; le poste le prendra à sa prochaine passe.' }
    : { done: false, ok: false, text: 'Création demandée…' }
}

/**
 * Fiche des FAITS MESURÉS remise à Ti-Coune (§20.30) : les mêmes paragraphes que les
 * réponses déterministes, qui ne contiennent que des valeurs relues sur le serveur.
 * Le modèle s'en sert comme unique source technique.
 */
export function factsSheet(f: ProjectFacts, now = Date.now(), fmt: Fmt = defaultFmt): string {
  const intents: Intent[] = f.backup ? ['status', 'why_alert', 'three_two_one', 'rto_rpo', 'last_test', 'schedule', 'recovery', 'todo', 'capabilities'] : ['status', 'capabilities']
  const seen = new Set<string>()
  const lines: string[] = []
  // Faits clés, sans ambiguïté, EN TÊTE : un petit modèle confond sinon « dernier test lancé à la main »
  // avec « tests non programmés ».
  const b = f.backup
  if (b?.hasSet) {
    const s = b.schedule
    lines.push('FAITS CLÉS (prioritaires)')
    lines.push(`- Tests de restauration AUTOMATIQUES : ${s.everyDays ? `OUI — programmés tous les ${s.everyDays} jours${s.nextDueAt ? `, prochain le ${fmt(s.nextDueAt)}` : ''}` : 'NON — aucun programmé (manuel seulement)'}.`)
    lines.push('- Un test peut aussi être lancé à la main à tout moment. Le mot « manuel » appliqué au DERNIER test décrit comment CE test a été lancé ; il ne dit rien sur la programmation.')
    if (b.lastTest) lines.push(`- Dernier test de restauration : ${b.lastTest.status === 'PASSED' ? 'réussi' : b.lastTest.status === 'FAILED' ? 'échoué' : 'en cours'}${b.lastTest.finishedAt ? `, le ${fmt(b.lastTest.finishedAt)}` : ''}.`)
    else lines.push('- Dernier test de restauration : aucun.')
    lines.push('', 'DÉTAIL')
  }
  for (const intent of intents) {
    // Les invites de l'interface (« demandez-moi… ») ne sont pas des faits : le modèle les recopierait.
    for (const p of answer(intent, f, now, fmt).paragraphs) if (!seen.has(p) && !/demandez-moi/i.test(p)) { seen.add(p); lines.push(`- ${p}`) }
  }
  return lines.join('\n')
}
